import { FC } from "react";

import { VStack, Box, useBreakpointValue, Flex } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import Head from "next/head";

import Message, { ContextMessage } from "@components/Message";
import { useFbPixel } from "@lib/fbPixel";
import { Desktop, Mobile } from "@lib/media";
import { RecaptchaProvider } from "@lib/recaptcha";
import colors from "@lib/theme/colors";
import { checkIfChatBotEnabled } from "@lib/webchat/utils/chatBotActions";
import { useFeatureData } from "@modules/features";
import { LAYOUT } from "@utils/constants";
import getEnv from "@utils/getEnv";

import Footer from "../Footer";
import Header from "../Header";
import { TLayout } from "./types";
import { useRouter } from "@lib/router";

const NextNProgress = dynamic(() => import("nextjs-progressbar"), { ssr: false });
const Webchat = dynamic(() => import("@lib/webchat"), { ssr: false });
const Scripts = dynamic(() => import("./Scripts"), { ssr: false });

const gtmCodes = {
  lt: 'GTM-MR68Z6X',
  lv: 'GTM-M5X5CB5',
  ee: 'GTM-TZR2X4T',
}

// Must execute BEFORE the GTM container loads.
const consentModeScript = `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}

  function getCookie(name) {
    var cookies = document.cookie.split(';');
    var cookieValue = null;
    cookies.forEach(function(cookie) {
      var parts = cookie.split('=').map(function(c) { return c.trim(); });
      if (parts[0] === name) {
        cookieValue = decodeURIComponent(parts[1]);
      }
    });
    return cookieValue;
  }

  function parseCookie(cookie) {
    try {
      var parsed = JSON.parse(cookie);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {}
    if (cookie === 'true' || cookie === 'false') return cookie === 'true';
    return cookie;
  }

  var parsedValue = parseCookie(getCookie('__cookie_consent'));
  var cookieArray = [];

  if (parsedValue !== null) {
    if (parsedValue === true || parsedValue === false) {
      cookieArray.push(parsedValue);
    } else {
      cookieArray.push.apply(cookieArray, parsedValue);
    }
  }

  var hasAll = cookieArray.indexOf(true) !== -1;
  var hasAnalytics = hasAll || cookieArray.indexOf('2') !== -1;
  var hasAds = hasAll || cookieArray.indexOf('3') !== -1;
  var hasPersonalization = hasAll || cookieArray.indexOf('4') !== -1;

  if (parsedValue === null) {
    gtag('consent', 'default', {
      'security_storage': 'granted',
      'functionality_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied',
      'personalization_storage': 'denied'
    });
  } else {
    gtag('consent', 'default', {
      'security_storage': 'granted',
      'functionality_storage': 'granted',
      'ad_storage': hasAds ? 'granted' : 'denied',
      'ad_user_data': hasAds ? 'granted' : 'denied',
      'ad_personalization': hasAds ? 'granted' : 'denied',
      'analytics_storage': hasAnalytics ? 'granted' : 'denied',
      'personalization_storage': hasPersonalization ? 'granted' : 'denied'
    });
  }
`;

const getGtmScript = (id: string) => `
  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${id}');
`;

const Layout: FC<TLayout> = ({
  children,
  domain,
  hasMobileFooter = true,
  hasTagManager = true,
  hasWebchat: isWithWebchat = true,
  hasMobileWebchat: isWithMobileWebchat = true,
  hasRecaptcha = false,
}) => {
  const { t } = useTranslation();

  useFbPixel();

  const isDesktop = useBreakpointValue({ base: false, md: true });
  const shouldShowFooter = isDesktop || hasMobileFooter;
  const features = useFeatureData();
  const isChatBotEnabled = checkIfChatBotEnabled(features);

  const { defaultLocale } = useRouter();

  const hasWebchat = isChatBotEnabled && getEnv("NEXT_PUBLIC_USE_WEBCHAT") === "true" && !hasRecaptcha && isWithWebchat;
  const shouldLoadTagManager = getEnv("NEXT_PUBLIC_USE_GTM") !== "false";

  return (
    <RecaptchaProvider isEnabled={hasRecaptcha} domain={domain}>
      <Head>
        {shouldLoadTagManager && defaultLocale && (
          <script key="consent-mode" dangerouslySetInnerHTML={{ __html: consentModeScript }} />
        )}
        {hasTagManager && shouldLoadTagManager && defaultLocale && (
          <script key="gtm" dangerouslySetInnerHTML={{ __html: getGtmScript(gtmCodes[defaultLocale]) }} />
        )}
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1" />
        <noscript>
          <style>
            {`
              .noscript-hide {
                display: none;
              }
            `}
          </style>
        </noscript>
      </Head>
      {defaultLocale && (
        <noscript
          dangerouslySetInnerHTML={{
            __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmCodes[defaultLocale]}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
          }}
        />
      )}
      <NextNProgress color={colors.red.lead} options={{ showSpinner: false }} />
      <VStack minH="100vh" justifyContent="flex-start" alignItems="stretch" spacing="0">
        <Box position="sticky" top="0" left="0" right="0" zIndex="100" background="white">
          <Header domain={domain} />
        </Box>

        <Flex direction="column" flexGrow={1} width="full">
          <noscript>
            <Message variant="danger" sx={{ mb: "0.5rem" }} textBoxWidth="71.25rem" justify="center" noScript>
              {t("common-errors-browser_error_message")}
            </Message>
          </noscript>
          <ContextMessage messageZone={LAYOUT.MESSAGE_ZONE_LAYOUT} />
          {children}
        </Flex>
        {shouldShowFooter && <Footer />}
      </VStack>
      {/**
       *
       * Place for additional scripts and script content containers.
       *
       */}
      {hasWebchat && (
        <Desktop>
          <Webchat />
        </Desktop>
      )}
      {hasWebchat && isWithMobileWebchat && (
        <Mobile>
          <Webchat />
        </Mobile>
      )}

      <Scripts hasTagManager={hasTagManager} />
    </RecaptchaProvider>
  );
};

export default Layout;
